/**
 * Floating bug-report button. 48x48 beetle icon with a notification dot.
 *
 * Position rules — driven by what other floating surfaces are open:
 *
 *   Default (nothing open)        right: 16, bottom: 130   (above journal icon)
 *   Bug drawer open               hidden
 *   Journal panel open only       right: 80, bottom:  20   (next to stat icon)
 *   Stat popup open only          right: 464, bottom: 70   (next to shifted journal)
 *   Journal + stat both open      hidden                   (no good spot, dismiss one to access)
 *
 * Receives the floating state from App.js as `journalOpen` and
 * `sheetOpen` props. The button itself doesn't subscribe to anything —
 * App.js owns those state flags and re-renders us when they change.
 */

import React from 'react';
import QuickReportOverlay from './QuickReportOverlay.jsx';
import { useUnreadBugReportCount, useIsDev } from '../hooks/useBugReports.js';

// Unified rule (see BUG-REPORT-REFERENCE.md §8):
//   - Default (nothing open): vertical stack — bug at right:16 bottom:130
//     above the journal icon at right:16 bottom:70.
//   - Any non-bug panel open → bug drops into the bottom row at slot B
//     (right:80 bottom:20). This includes the case where journal AND
//     stat are both open: stat popup ascends to "center", journal panel
//     shifts left, the bottom row stays clear so the bug icon fits.
//   - Bug drawer open → bug icon hidden (drawer IS the bug surface).
const computePosition = ({ drawerOpen, journalOpen, sheetOpen }) => {
  if (drawerOpen) return { hide: true };
  if (journalOpen || sheetOpen) return { hide: false, right: '80px', bottom: '20px' };
  return { hide: false, right: '16px', bottom: '130px' };
};

export default function QuickReportButton({
  context,
  onNavigateToReports,
  onNavigateToDev,
  journalOpen,
  sheetOpen,
  onCloseOtherSurfaces,
}) {
  const [open, setOpen] = React.useState(false);
  const unread = useUnreadBugReportCount();
  const showBadge = typeof unread === 'number' && unread > 0;
  const isDev = useIsDev();

  const pos = computePosition({ drawerOpen: open, journalOpen: !!journalOpen, sheetOpen: !!sheetOpen });

  return (
    <>
      {!pos.hide && <div
        data-bug-report-button
        style={{
          position: 'fixed',
          right: pos.right,
          bottom: pos.bottom,
          zIndex: 1500,
          transition: 'right 200ms ease-out, bottom 200ms ease-out',
        }}
      >
        <button
          onClick={() => setOpen(true)}
          title="Report a bug, suggestion, or question"
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #6b1e1e 0%, #3a1414 100%)',
            border: '2px solid #ff6961',
            color: '#ffd0cc',
            fontSize: '1.3rem',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s',
            position: 'relative',
          }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
        >
          🪲
          {showBadge && (
            <span
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                padding: '0 4px',
                borderRadius: '50%',
                background: '#ff6961',
                color: '#fff',
                fontSize: '0.55rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </div>}

      <QuickReportOverlay
        open={open}
        onClose={() => setOpen(false)}
        context={context}
        onNavigateToReports={onNavigateToReports}
        onNavigateToDev={onNavigateToDev}
        onCloseOtherSurfaces={onCloseOtherSurfaces}
        isDev={!!isDev}
      />
    </>
  );
}
